import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, MapPin, X } from 'lucide-react';
const NAMES = ['María Belén', 'Sofía', 'Lucía', 'Valentina', 'Camila', 'Florencia', 'Agustina', 'Martina', 'Carolina', 'Mariana', 'Julieta', 'Romina', 'Daniela', 'Gabriela', 'Paula', 'Andrea', 'Verónica', 'Natalia', 'Cecilia', 'Mariela', 'Facundo', 'Matías', 'Joaquín', 'Nicolás', 'Lautaro', 'Tomás', 'Santiago', 'Bautista', 'Mateo', 'Federico', 'Sebastián', 'Lucas', 'Diego', 'Martín', 'Pablo', 'Esteban', 'Gonzalo', 'Hernán', 'Maximiliano', 'Leandro'];
const LAST_INITIALS = ['G.', 'M.', 'R.', 'S.', 'L.', 'P.', 'F.', 'V.', 'T.', 'B.', 'C.', 'A.', 'N.', 'D.', 'H.', 'O.'];
const CITIES = ['Buenos Aires', 'CABA', 'La Plata', 'Mar del Plata', 'Bahía Blanca', 'Rosario', 'Córdoba', 'Mendoza', 'San Miguel de Tucumán', 'Salta', 'Neuquén', 'Bariloche', 'San Juan', 'Santa Fe', 'Resistencia', 'Corrientes', 'Posadas', 'Paraná', 'Río Cuarto', 'San Luis', 'Jujuy', 'Comodoro Rivadavia', 'Ushuaia', 'La Rioja', 'Catamarca'];
const OFFERS = [{
  name: 'Opción Estándar',
  price: '$19.990'
}, {
  name: 'Opción Élite',
  price: '$27.990'
}, {
  name: 'Opción Élite',
  price: '$27.990'
} // weighted toward Élite
];
const TIME_AGO = ['hace unos segundos', 'hace 1 min', 'hace 2 min', 'hace 3 min', 'hace 5 min', 'hace 7 min'];
const pick = <T,>(arr: readonly T[] | T[]): T => arr[Math.floor(Math.random() * arr.length)];
interface Purchase {
  id: number;
  name: string;
  city: string;
  offer: string;
  price: string;
  timeAgo: string;
}
const generatePurchase = (): Purchase => {
  const offer = pick(OFFERS);
  return {
    id: Date.now() + Math.random(),
    name: `${pick(NAMES)} ${pick(LAST_INITIALS)}`,
    city: pick(CITIES),
    offer: offer.name,
    price: offer.price,
    timeAgo: pick(TIME_AGO)
  };
};
export const PurchaseToast = () => {
  const [current, setCurrent] = useState<Purchase | null>(null);
  useEffect(() => {
    let showTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;
    const showNext = () => {
      setCurrent(generatePurchase());
      hideTimer = setTimeout(() => {
        setCurrent(null);
        // Schedule next between 40s and 60s after dismissal
        showTimer = setTimeout(showNext, 40000 + Math.random() * 20000);
      }, 6000); // visible for 6s
    };
    // First toast appears between 8s and 12s after page load
    showTimer = setTimeout(showNext, 8000 + Math.random() * 4000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);
  const handleDismiss = () => setCurrent(null);
  return <div aria-live="polite" className="fixed left-3 bottom-3 md:left-6 md:bottom-6 z-[60] pointer-events-none" data-id="element-250">
      <AnimatePresence data-id="element-251">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto w-[280px] md:w-[320px] bg-cream rounded-sm shadow-2xl border border-gold-500/30 overflow-hidden" data-id="element-252">
            <div className="flex items-stretch gap-3 p-3" data-id="element-253">
              {/* Icon */}
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center" data-id="element-254">
                <ShieldCheck className="w-5 h-5 text-gold-600" data-id="element-255" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pr-2" data-id="element-256">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-257">
                  <span className="text-[9px] uppercase tracking-widest text-gold-600 font-bold" data-id="element-258">
                    ✓ Compra verificada
                  </span>
                </div>
                <p className="text-sm text-navy font-serif font-bold leading-tight truncate" data-id="element-259">
                  {current.name}
                </p>
                <p className="text-xs text-navy/70 leading-tight" data-id="element-260">
                  compró la{' '}
                  <span className="font-semibold text-navy" data-id="element-261">
                    {current.offer}
                  </span>
                </p>
                <div className="flex items-center gap-1 mt-1.5 text-[10px] text-navy/55" data-id="element-262">
                  <MapPin className="w-3 h-3" data-id="element-263" />
                  <span className="truncate" data-id="element-264">{current.city}</span>
                  <span className="opacity-50" data-id="element-265">·</span>
                  <span data-id="element-266">{current.timeAgo}</span>
                </div>
              </div>

              {/* Dismiss */}
              <button onClick={handleDismiss} aria-label="Cerrar notificación" className="flex-shrink-0 self-start text-navy/30 hover:text-navy/70 transition-colors p-0.5" data-id="element-267">
                <X className="w-3.5 h-3.5" data-id="element-268" />
              </button>
            </div>

            {/* Progress bar (visual cue of auto-dismiss) */}
            <motion.div initial={{
          width: '100%'
        }} animate={{
          width: '0%'
        }} transition={{
          duration: 6,
          ease: 'linear'
        }} className="h-0.5 bg-gold-500" data-id="element-269" />
          </motion.div>}
      </AnimatePresence>
    </div>;
};